import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Slider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import PWAHeader from "../common/PWAHeader";
import DatePickerRow from "../common/DatePickerRow";
import PrimaryButton from "../common/PrimaryButton";

const mealTypes = [
  { type: "BREAKFAST", icon: <FreeBreakfastIcon fontSize="small" />, defaultTime: "08:00" },
  { type: "LUNCH", icon: <LunchDiningIcon fontSize="small" />, defaultTime: "13:00" },
  { type: "DINNER", icon: <DinnerDiningIcon fontSize="small" />, defaultTime: "20:00" },
  { type: "SNACKS", icon: <DinnerDiningIcon fontSize="small" />, defaultTime: "16:00" },
  { type: "SKIPPED", icon: <DoNotDisturbIcon fontSize="small" />, defaultTime: "00:00" },
];

const Fsn24LogFood = ({ onTap }) => {
  const [currentWeek, setCurrentWeek] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMealType, setSelectedMealType] = useState("SNACKS");
  const [description, setDescription] = useState("");
  const [foodType, setFoodType] = useState("Home Food");
  const [portionSize, setPortionSize] = useState(50);
  const [healthMeter, setHealthMeter] = useState(50);
  const [logTime, setLogTime] = useState(new Date());

  const handleMealTypeChange = (type) => {
    setSelectedMealType(type);
    const defaultTime = mealTypes.find((meal) => meal.type === type)?.defaultTime || "00:00";
    const [hours, minutes] = defaultTime.split(":");
    const updatedTime = new Date(logTime);
    updatedTime.setHours(hours, minutes);
    setLogTime(updatedTime);
  };

  const handleSubmit = async () => {
    const payload = {
      data: JSON.stringify([
        {
          createdTime: `${selectedDate} ${logTime.toLocaleTimeString()}`,
          times: logTime.toLocaleTimeString(),
          date: selectedDate,
          foodName: description,
          localFoodId: 1,
          mealType: selectedMealType,
          recognition: 0,
          healthAnalysis: {
            foodType: foodType === "Home Food" ? "Home" : "Outside",
            portionSize,
            portionCategory: portionSize > 50 ? "Large" : "Medium",
            healthMeter,
            healthMeterCategory: healthMeter > 50 ? "High" : "Medium",
          },
        },
      ]),
    };
    console.log(payload);

  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PWAHeader title={onTap?.FAI?.title || "Log New Food"} />
      

        <DatePickerRow
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          currentWeek={currentWeek}
          setCurrentWeek={setCurrentWeek}
          restrictPastDates={false}  // Restrict past dates for appointment booking
          restrictFutureDates={true} // Allow future dates
        />

        <Box sx={{ padding: "0 16px" }}>
          {/* Meal Type Selection with Icons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              margin: "16px 0",
              gap: "12px",
              flexWrap: "nowrap",
            }}
          >
            {mealTypes.map(({ type, icon }) => (
              <Box
                key={type}
                onClick={() => handleMealTypeChange(type)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: selectedMealType === type ? "#4CAF50" : "#FFF",
                    border: `2px solid ${selectedMealType === type ? "#4CAF50" : "#CCC"}`,
                    color: selectedMealType === type ? "#FFF" : "#666",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: selectedMealType === type ? "0px 4px 6px rgba(0, 0, 0, 0.1)" : "none",
                    "&:hover": {
                      backgroundColor: selectedMealType === type ? "#45A049" : "#E8F5E9",
                    },
                  }}
                >
                  {icon}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    marginTop: "5px",
                    textAlign: "center",
                    color: selectedMealType === type ? "#4CAF50" : "#666",
                  }}
                >
                  {type}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Food Description and Log Time */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <TextField
              label="Describe your food"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              inputProps={{
                maxLength: 60,
              }}
              sx={{
                marginRight: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TimePicker
                value={logTime}
                onChange={(newTime) => setLogTime(newTime)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      width: "100px",
                      "& .MuiInputBase-input": { textAlign: "center" },
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Food Type Selection */}
          <RadioGroup
            row
            value={foodType}
            onChange={(e) => setFoodType(e.target.value)}
            sx={{ marginBottom: "16px" }}
          >
            <FormControlLabel
              value="Home Food"
              control={<Radio sx={{ color: "#4CAF50", "&.Mui-checked": { color: "#4CAF50" } }} />}
              label="Home Food"
            />
            <FormControlLabel
              value="Outside Food"
              control={<Radio sx={{ color: "#4CAF50", "&.Mui-checked": { color: "#4CAF50" } }} />}
              label="Outside Food"
            />
          </RadioGroup>

          {/* Portion Size Slider */}
          <Typography variant="subtitle1" sx={{ marginBottom: "8px", color: "#4CAF50" }}>
            Portion Size
          </Typography>
          <Slider
            value={portionSize}
            onChange={(e, value) => setPortionSize(value)}
            min={0}
            max={100}
            sx={{
              marginBottom: "16px",
              "& .MuiSlider-thumb": { bgcolor: "#4CAF50" },
              "& .MuiSlider-track": { bgcolor: "#4CAF50" },
            }}
          />

          {/* Health Meter Slider */}
          <Typography variant="subtitle1" sx={{ marginBottom: "8px", color: "#4CAF50" }}>
            Health Meter
          </Typography>
          <Slider
            value={healthMeter}
            onChange={(e, value) => setHealthMeter(value)}
            min={0}
            max={100}
            sx={{
              marginBottom: "24px",
              "& .MuiSlider-thumb": { bgcolor: healthMeter < 50 ? "error.main" : "#4CAF50" },
              "& .MuiSlider-track": { bgcolor: healthMeter < 50 ? "error.main" : "#4CAF50" },
            }}
          />

          {/* Log Button */}
          <PrimaryButton onClick={handleSubmit} label="Log" />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Fsn24LogFood;